import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import { i18nInstance } from "../i18n";
import type { TextDirection } from "../../core";

interface LocaleState {
    language: string;
    direction: TextDirection;
    timezone: string;
    locale: string;
}

export interface LocaleManager extends LocaleState {
    setLanguage(code: string, direction?: TextDirection): void;
    setTimezone(timezone: string): void;
}

const LocaleContext = createContext<LocaleManager | null>(null);

// The central locale/RTL/time authority (architecture.md §10). The one effect that
// flips `<html lang/dir>` and the i18n catalog. Timezone is consumed by date widgets.
export function LocaleProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<LocaleState>({
        language: "en",
        direction: "ltr",
        timezone: "UTC",
        locale: "en",
    });

    useEffect(() => {
        const root = document.documentElement;
        root.lang = state.language;
        root.dir = state.direction;
        void i18nInstance.changeLanguage(state.language);
    }, [state.language, state.direction]);

    // Stable setter refs — without these, every state change recreates the
    // setter via useMemo, which would re-fire any consumer effect that depends
    // on the setter (e.g. PreferenceSync), in turn calling the setter again
    // and looping infinitely. useCallback([]) gives them React-state-stable
    // identity for the lifetime of the provider.
    const setLanguage = useCallback<LocaleManager["setLanguage"]>(
        (code, direction) =>
            setState((s) => ({ ...s, language: code, locale: code, direction: direction ?? s.direction })),
        [],
    );
    const setTimezone = useCallback<LocaleManager["setTimezone"]>(
        (timezone) => setState((s) => ({ ...s, timezone })),
        [],
    );

    const value = useMemo<LocaleManager>(
        () => ({ ...state, setLanguage, setTimezone }),
        [state, setLanguage, setTimezone],
    );

    return (
        <I18nextProvider i18n={i18nInstance}>
            <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
        </I18nextProvider>
    );
}

export function useLocale(): LocaleManager {
    const ctx = useContext(LocaleContext);
    if (!ctx) throw new Error("useLocale must be used within a LocaleProvider");
    return ctx;
}

export function useT(namespace = "common") {
    return useTranslation(namespace).t;
}
